import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { KeyRound, User, Mail, Lock, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MyAccountPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me`);
        setName(res.data.name || '');
        setEmail(res.data.email || '');
        setRole(res.data.role || '');
      } catch (err) { logger.error('Failed to load profile:', err); }
      setLoading(false);
    };
    fetchMe();
  }, []);

  const handleSave = async () => {
    if (!currentPassword) {
      toast.error('Enter your current password to make changes');
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword && newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (name) payload.name = name;
      if (email) payload.email = email;
      if (newPassword) payload.new_password = newPassword;

      const res = await axios.put(`${API_URL}/api/auth/update-profile`, payload);
      toast.success(res.data.message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
    setSaving(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg mx-auto">
        <Link to="/" className="text-xs text-slate-400 hover:text-[#E07A3A] tracking-wider uppercase flex items-center gap-1 mb-6">
          <ArrowLeft className="h-3 w-3" /> Back
        </Link>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#0F172A] flex items-center justify-center">
              <KeyRound className="h-5 w-5 text-[#E07A3A]" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#0F172A]">My Account</h1>
              <p className="text-xs text-slate-400 tracking-wider uppercase">{role}</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-2">Update your name, email, or password.</p>
        </div>

        <div className="bg-white border border-slate-100 p-6 sm:p-8 space-y-5" data-testid="my-account-form">
          <div>
            <label className="text-xs text-slate-500 tracking-wider uppercase font-medium flex items-center gap-1 mb-1">
              <User className="h-3 w-3" /> Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-700 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
              data-testid="account-name"
            />
          </div>

          <div>
            <label className="text-xs text-slate-500 tracking-wider uppercase font-medium flex items-center gap-1 mb-1">
              <Mail className="h-3 w-3" /> Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-700 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
              data-testid="account-email"
            />
          </div>

          <hr className="border-slate-100" />

          <div>
            <label className="text-xs text-slate-500 tracking-wider uppercase font-medium flex items-center gap-1 mb-1">
              <Lock className="h-3 w-3" /> New Password <span className="text-slate-300 normal-case">(leave blank to keep current)</span>
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-700 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
              data-testid="account-new-password"
            />
          </div>

          {newPassword && (
            <div>
              <label className="text-xs text-slate-500 tracking-wider uppercase font-medium mb-1 block">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-700 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
                data-testid="account-confirm-password"
              />
            </div>
          )}

          <hr className="border-slate-100" />

          <div>
            <label className="text-xs text-red-500 tracking-wider uppercase font-medium flex items-center gap-1 mb-1">
              <Lock className="h-3 w-3" /> Current Password <span className="text-red-300 normal-case">(required)</span>
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password to save"
              className="w-full bg-slate-50 border border-red-200 px-4 py-2.5 text-sm text-slate-700 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
              data-testid="account-current-password"
            />
          </div>

          <Button
            onClick={handleSave}
            disabled={saving || !currentPassword}
            className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-wider px-8 py-5 w-full"
            data-testid="save-account-btn"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MyAccountPage;
